import { Link } from 'react-router-dom';
import { BadgeCheck, Store, Users, Wrench, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import CTABand from '../components/CTABand';
import { Reveal, Chapter } from '../components/motion';
import { IMAGES, localBusinessSchema, breadcrumbSchema } from '../data/site';

const VALUES = [
  { icon: BadgeCheck, title: 'Workmanship', text: 'We size systems correctly, install them carefully, and do the job right the first time. Our name goes on every invoice — and we have protected it for over 60 years.' },
  { icon: Users, title: 'Trained Technicians', text: 'Our service and installation teams are trained professionals, factory-trained as an American Standard Customer Care Dealer and experienced across all major brands.' },
  { icon: Wrench, title: 'Full-Service Trades', text: 'Heating, air conditioning, plumbing, excavation, and trenching under one roof — residential, commercial, industrial, and institutional.' },
  { icon: Store, title: 'A Real Local Storefront', text: 'Our hardware and garden store at 204 West Stanly Street in Stanfield keeps us rooted in the community we serve. Stop in and see us.' },
];

export default function About() {
  return (
    <>
      <SEO
        title="About B.E. Holbrooks Co. | HVAC & Plumbing Company in Stanfield, NC"
        description="Learn about B.E. Holbrooks Co., a trusted HVAC, plumbing, excavation, and home comfort company serving Stanfield, NC and surrounding communities for over 60 years."
        path="/about"
        schemas={[
          localBusinessSchema,
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }]),
        ]}
      />

      <PageHero
        eyebrow="Our Story"
        h1Lines={['About', 'B.E. Holbrooks Co.']}
        sub="A family-owned heating, air conditioning, plumbing, and excavation company serving Stanfield and the greater Charlotte area for over 60 years."
        image={IMAGES.storefront}
        imageAlt="B.E. Holbrooks Co. storefront on West Stanly Street in Stanfield, NC"
        crumbs={[{ name: 'About' }]}
      />

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <Reveal>
            <Chapter num="01" label="Local History" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              Built on Workmanship, Kept by Trust
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              B.E. Holbrooks Co. has provided plumbing and heating & air services for over 60 years. What started
              as a local trade shop on West Stanly Street has grown into a full-service comfort company — but the
              way we do business has never changed: listen to the customer, evaluate honestly, and find the right
              solution for the home or building.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
              Over six decades, we have earned a reputation that keeps growing our customer base — by making
              customer satisfaction the top priority on every call, from a dripping faucet to a complete commercial
              mechanical installation.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
              We were hand-picked by American Standard to be one of their Customer Care Dealers, a recognition of
              installation quality and customer care that we work to uphold every day.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-24 w-24 -skew-x-12 rounded-lg bg-brand/15" aria-hidden="true" />
              <img
                src={IMAGES.storefront}
                alt="The B.E. Holbrooks Co. hardware and garden store, a Stanfield NC landmark for over 60 years"
                className="clip-frame relative w-full rounded-xl object-cover shadow-2xl shadow-navy/20"
                data-testid="about-storefront-image"
              />
            </div>
            <img
              src={IMAGES.americanStandard}
              alt="American Standard Customer Care Dealer - B.E. Holbrooks Co."
              className="mt-6 w-2/3 rounded-xl bg-white p-3 shadow-lg"
              data-testid="about-american-standard-image"
            />
          </Reveal>
        </div>
      </section>

      {/* HISTORY */}
      <section className="relative overflow-hidden bg-navy py-16 grain lg:py-24" data-testid="about-history">
        <div className="absolute -left-24 top-0 h-full w-72 -skew-x-12 bg-brand/10" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Chapter num="02" label="Our History" light />
            <h2 className="mt-4 max-w-3xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Our History: Over a Century of Honest Work in Stanfield
            </h2>
          </Reveal>

          <div className="mt-14 grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <h3 className="font-display text-lg font-bold text-sky-300">Humble Beginnings</h3>
              <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">
                Bruner Edward Holbrooks, our founder, was born in 1891 into very humble circumstances. Orphaned at
                the age of five, he was raised by his grandparents before eventually moving into an orphanage.
                Around the age of eighteen, he relocated to Stanfield, Stanly County, where he opened a sawmill
                with a business partner.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">
                In 1913, Bruner married his wife, Martha. Their first home was an old train car — a photograph of
                it still hangs in our office today as a reminder of the humble beginnings from which our company grew.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <figure className="overflow-hidden rounded-2xl border border-white/15 bg-white/5 shadow-2xl">
                <img
                  src={IMAGES.historyFounders}
                  alt="Bruner Edward Holbrooks and his wife Martha, founders of B.E. Holbrooks Co. in Stanfield, NC"
                  className="w-full object-cover"
                  loading="lazy"
                  data-testid="history-photo-founders"
                />
                <figcaption className="px-4 py-3 font-mono text-xs uppercase tracking-widest text-slate-300">
                  Bruner & Martha Holbrooks
                </figcaption>
              </figure>
            </Reveal>
          </div>

          <Reveal className="mx-auto mt-16 max-w-3xl">
            <h3 className="font-display text-lg font-bold text-sky-300">The Heart of the Community</h3>
            <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">
              Before or around 1920, Bruner opened a hardware store in Stanfield. Through determination, hard work,
              and the support of Martha and their seven children, the business steadily expanded beyond hardware.
              Over the years, it came to offer farm and home merchandise, groceries, fresh meat, blacksmith services,
              and a gas station that even handled minor automobile repairs. Bruner also provided seed-cleaning
              services for local farmers. For many residents, the store became the heart of the community and an
              important gathering place.
            </p>
          </Reveal>

          <div className="mt-16 grid items-center gap-10 lg:grid-cols-2">
            <Reveal className="lg:order-2">
              <h3 className="font-display text-lg font-bold text-sky-300">A Patented Innovation</h3>
              <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">
                In 1938, Bruner designed and manufactured a seed pan harvester to help farmers efficiently gather
                lespedeza seed. In recognition of his innovation, he was awarded a patent for the machine in 1945.
                The seed pans were manufactured at our current location and shipped throughout the United States —
                by our estimates, as many as twenty-two pans were produced each day. We find this a remarkable
                accomplishment that remains a source of pride for our company.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">
                Around the same time, B.E. Holbrooks began offering plumbing and heating services. The company's
                telephone number was the first in the area: #1.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:order-1">
              <figure className="overflow-hidden rounded-2xl border border-white/15 bg-white/5 shadow-2xl">
                <img
                  src={IMAGES.historyTruck}
                  alt="Vintage B.E. Holbrooks Co. truck loaded with patented seed pan harvesters built in Stanfield, NC in the 1940s"
                  className="w-full object-cover"
                  loading="lazy"
                  data-testid="history-photo-truck"
                />
                <figcaption className="px-4 py-3 font-mono text-xs uppercase tracking-widest text-slate-300">
                  Seed pans shipped nationwide, c. 1940s
                </figcaption>
              </figure>
            </Reveal>
          </div>

          <div className="mt-16 grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <h3 className="font-display text-lg font-bold text-sky-300">Three Generations Later</h3>
              <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">
                Built on honest business practices and a commitment to serving the community, the company continued
                to grow and eventually shifted its primary focus to residential and commercial plumbing and HVAC
                services. Following Bruner's passing in 1952, his son Bill assumed leadership of the business and
                successfully guided it for decades until his retirement in the late 2000s.
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-300 sm:text-lg">
                Today, Bill's sons, Brad and Brent, continue the work their grandfather began more than a century
                ago. While much has changed since Bruner first arrived in Stanfield, the values that built this
                company remain the same. At B.E. Holbrooks, we take great pride in our history. We would not have
                served our community for more than one hundred years without treating our neighbors with honesty,
                respect, and fairness. Those principles guided Bruner from the beginning, and they continue to
                guide us today.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <figure className="overflow-hidden rounded-2xl border border-white/15 bg-white/5 shadow-2xl">
                <img
                  src={IMAGES.historyWorkshop}
                  alt="Workers manufacturing seed pan harvesters inside the B.E. Holbrooks Co. shop in Stanfield, NC"
                  className="w-full object-cover"
                  loading="lazy"
                  data-testid="history-photo-workshop"
                />
                <figcaption className="px-4 py-3 font-mono text-xs uppercase tracking-widest text-slate-300">
                  Manufacturing at our current location
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-brand-soft/50 py-16 lg:py-24" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Chapter num="03" label="What We Stand For" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              Over a Century of the Same Promise
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1.5 hover:shadow-lg" data-testid={`value-card-${i}`}>
                  <span className="inline-flex rounded-lg bg-brand p-2.5 text-white"><v.icon className="h-5 w-5" /></span>
                  <h3 className="mt-4 font-display text-lg font-bold text-navy">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Chapter num="04" label="Residential & Commercial" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">
              Serving Homes, Businesses & Institutions
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              Our work spans residential service calls, custom homes, new construction, commercial shell and upfit
              plumbing, and HVAC for offices, retail, churches, and industrial facilities across Stanly County and
              the greater Charlotte area. Whether you own a century-old farmhouse or manage a commercial building,
              you get the same trained technicians and the same accountable local company.
            </p>
            <Link to="/contact" data-testid="about-contact-link" className="mt-7 inline-flex items-center gap-1 font-display text-sm font-bold text-brand hover:text-brand-dark">
              Talk with our team <ChevronRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTABand heading="Work With a Company That Has 60+ Years Behind It" testid="cta-about" />
    </>
  );
}
